type ApiErrorData = {
  message?: unknown;
  error?: unknown;
  errors?: unknown;
};

const COMMON_ERROR_MESSAGES: Record<string, string> = {
  'An error occurred': 'エラーが発生しました。',
  'An unexpected error occurred': '予期しないエラーが発生しました。',
  'Unknown error': '不明なエラーが発生しました。',
  'Request failed': 'リクエストに失敗しました。',
  'Unauthenticated.': 'ログインが必要です。再度ログインしてください。',
  'Network Error': 'ネットワークエラーが発生しました。通信環境を確認して再度お試しください。',
  'Failed to fetch': 'データの取得に失敗しました。',
  'Failed to load feature flags': '機能設定の読み込みに失敗しました。',
  'Failed to load test results': 'テスト結果の読み込みに失敗しました。',
  'Failed to save style': 'スタイルの保存に失敗しました。',
  'Failed to get image data': '画像データの取得に失敗しました。',
  'Failed to fetch execution status': '実行ステータスの取得に失敗しました。',
  'Failed to fetch product status': '商品のステータス取得に失敗しました。',
  'Failed to cache image': '画像のキャッシュに失敗しました。',
  'Delete failed': '削除に失敗しました。',
  'Update failed': '更新に失敗しました。',
  'Save failed': '保存に失敗しました。',
  'Request failed with status code 400': 'リクエスト内容が正しくありません。',
  'Request failed with status code 401': 'ログインが必要です。再度ログインしてください。',
  'Request failed with status code 403': 'この操作を行う権限がありません。',
  'Request failed with status code 404': '対象のデータが見つかりません。',
  'Request failed with status code 409': 'データが競合しています。最新の状態を確認して再度お試しください。',
  'Request failed with status code 422': '入力内容に誤りがあります。',
  'Request failed with status code 429': 'リクエストが多すぎます。しばらく待ってから再度お試しください。',
  'Request failed with status code 500': 'サーバーエラーが発生しました。しばらく待ってから再度お試しください。',
  'Request failed with status code 502': 'サーバーに一時的な問題が発生しています。しばらく待ってから再度お試しください。',
  'Request failed with status code 503': 'サービスが一時的に利用できません。しばらく待ってから再度お試しください。',
  'Request failed with status code 504': 'サーバーの応答がタイムアウトしました。しばらく待ってから再度お試しください。',
};

const FIELD_LABELS: Record<string, string> = {
  email: 'メールアドレス',
  password: 'パスワード',
  password_confirmation: '確認用パスワード',
  name: '名前',
  title: 'タイトル',
  url: 'URL',
  token: 'トークン',
  file: 'ファイル',
  image: '画像',
  prompt: 'プロンプト',
  message: 'メッセージ',
};

const hasJapanese = (message: string): boolean => /[\u3040-\u30ff\u3400-\u9fff]/.test(message);

const isMachineReadableCode = (message: string): boolean => /^[a-z0-9_.:-]+$/.test(message);

const labelFor = (field: string): string => FIELD_LABELS[field] || field.replace(/_/g, ' ');

export const translateErrorMessage = (message: unknown): unknown => {
  if (typeof message !== 'string') {
    return message;
  }

  const trimmed = message.trim();
  if (!trimmed || hasJapanese(trimmed) || isMachineReadableCode(trimmed)) {
    return message;
  }

  if (COMMON_ERROR_MESSAGES[trimmed]) {
    return COMMON_ERROR_MESSAGES[trimmed];
  }

  const requiredMatch = trimmed.match(/^The (.+) field is required\.$/i);
  if (requiredMatch) {
    return `${labelFor(requiredMatch[1])}を入力してください。`;
  }

  const invalidMatch = trimmed.match(/^The selected (.+) is invalid\.$/i);
  if (invalidMatch) {
    return `選択された${labelFor(invalidMatch[1])}は有効ではありません。`;
  }

  const emailMatch = trimmed.match(/^The (.+) field must be a valid email address\.$/i);
  if (emailMatch) {
    return `${labelFor(emailMatch[1])}には有効なメールアドレスを指定してください。`;
  }

  const urlMatch = trimmed.match(/^The (.+) field must be a valid URL\.$/i);
  if (urlMatch) {
    return `${labelFor(urlMatch[1])}には有効なURLを指定してください。`;
  }

  const maxStringMatch = trimmed.match(/^The (.+) field must not be greater than (\d+) characters\.$/i);
  if (maxStringMatch) {
    return `${labelFor(maxStringMatch[1])}は${maxStringMatch[2]}文字以下にしてください。`;
  }

  const minStringMatch = trimmed.match(/^The (.+) field must be at least (\d+) characters\.$/i);
  if (minStringMatch) {
    return `${labelFor(minStringMatch[1])}は${minStringMatch[2]}文字以上にしてください。`;
  }

  const statusMatch = trimmed.match(/^Request failed with status code (\d+)$/i);
  if (statusMatch) {
    return `リクエストに失敗しました。（ステータスコード: ${statusMatch[1]}）`;
  }

  return message;
};

const translateValue = (value: unknown): unknown => {
  if (typeof value === 'string') {
    return translateErrorMessage(value);
  }

  if (Array.isArray(value)) {
    return value.map(translateValue);
  }

  if (value && typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>).reduce<Record<string, unknown>>(
      (translated, [key, nestedValue]) => {
        translated[key] = translateValue(nestedValue);
        return translated;
      },
      {}
    );
  }

  return value;
};

export const normalizeApiErrorData = <T extends ApiErrorData | unknown>(data: T): T => {
  if (!data || typeof data !== 'object') {
    return data;
  }

  const normalized = { ...(data as Record<string, unknown>) };

  if ('message' in normalized) {
    normalized.message = translateErrorMessage(normalized.message);
  }

  if ('error' in normalized) {
    const errorValue = normalized.error;
    normalized.error = typeof errorValue === 'string' && isMachineReadableCode(errorValue)
      ? errorValue
      : translateValue(errorValue);
  }

  if ('errors' in normalized) {
    normalized.errors = translateValue(normalized.errors);
  }

  return normalized as T;
};

export const getJapaneseErrorMessage = (error: any, fallback = 'エラーが発生しました。'): string => {
  const data = normalizeApiErrorData(error?.response?.data);
  const message = data?.message || data?.error || error?.message || fallback;
  const translated = translateErrorMessage(message);
  return typeof translated === 'string' ? translated : fallback;
};
